import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Save, PlayCircle, Plus, Trash2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { PageHeader, FormActionBar, AppButton, ResetButton, Callout, reactSelectTheme } from "../ui";
import { resolveSessionKind } from "./sessionKind";
import { can } from "../../utils/access";
import EventPageSection from "./event/EventPageSection";
import EventProgramSection from "./event/EventProgramSection";
import EventAutomationSection from "./event/EventAutomationSection";
import EventCertificateSection from "./event/EventCertificateSection";
import EventAiPanel from "./event/EventAiPanel";
import {
  newEventFields, eventFieldsFromSession, eventPayload, previewsFromSession,
  validateEventFields, POST_END_FIELDS, toLocalInput, fromLocalInput,
} from "./event/eventForm";
import "./demo-classes.scss";

const getDemoSession = (data) => data?.session || data?.item || data;

const initialForm = {
  title: "",
  description: "",
  scheduledStartAt: "",
  scheduledEndAt: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
  instructorId: "",
  coHostInstructorIds: [],
  batchId: "",
  courseId: "",
  joinOpensMinutesBefore: 15,
  allowJoinAfterStart: true,
  maxAttendees: 100,
  registrationMode: "preRegister",
  collectEmail: true,
  collectCity: false,
  consentText: "",
  isPublished: true,
  requireCaptcha: false,
  customQuestions: [],
  branding: { logoUrl: "", coverImageUrl: "", primaryColor: "", tagline: "" },
  recording: { enabled: false, visibility: "admin", url: "" },
  /* What ATTENDEES may do once they are in the room. These defaults are the
     permissions every demo and webinar has always run with — mic and camera
     off, chat and raise-hand on — so a form left alone produces exactly the
     session it used to. Speakers are never governed by this. */
  attendeePolicy: {
    allowMicrophone: false,
    allowCamera: false,
    allowScreenShare: false,
    allowChat: true,
    allowRaiseHand: true,
    startMuted: true,
  },
};

/* The attendee permissions, in the order they are shown. `startMuted` is
   deliberately absent: it is meaningful only when the microphone is allowed at
   all, so it is rendered as a dependent line under the mic toggle rather than
   as a sixth equal checkbox. */
const ATTENDEE_PERMISSIONS = [
  {
    key: "allowMicrophone",
    label: "Turn on their microphone",
    hint: "Off for a webinar in most cases. The host can still unmute one person to take a question.",
  },
  {
    key: "allowCamera",
    label: "Turn on their camera",
    hint: "Needs to be enabled for your account as well — ask support if the toggle appears to do nothing.",
  },
  {
    key: "allowChat",
    label: "Send chat messages",
    hint: "Speakers can always post in chat, whatever this says.",
  },
  {
    key: "allowScreenShare",
    label: "Share their screen",
    hint: "Rarely wanted from a public audience.",
  },
  {
    key: "allowRaiseHand",
    label: "Raise their hand",
    hint: "How an attendee asks for the floor when their microphone is off.",
  },
];

/** Create / edit for BOTH live-session products — `kind` picks the endpoint,
 *  the redirect target and every visible word. */
const DemoClassesFormPage = ({ kind = "demo" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const vocab = resolveSessionKind(kind);
  const canManage = can("liveEvents.manage");

  // The default consent text is the one thing in `initialForm` that differs
  // per product, so it is applied here rather than baked into the constant.
  // A webinar also carries its event page, reminders and certificate
  // (./event/eventForm) — a demo class keeps exactly the form it had.
  const isWebinar = vocab.kind === "webinar";
  const [form, setForm] = useState({
    ...initialForm,
    consentText: vocab.copy.consentDefault,
    ...(isWebinar ? newEventFields() : {}),
  });
  // Resolved URLs for uploaded images, keyed by storage key — previews only.
  const [previews, setPreviews] = useState({});
  const [websiteUrl, setWebsiteUrl] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [errors, setErrors] = useState({});
  // Which platform the institute runs its live classes on. Only ever used to
  // choose between two hints — an admin should not have to discover from a
  // customer that a permission they ticked is advisory on Zoom.
  const [meetingProvider, setMeetingProvider] = useState(null);
  // The permissions are stamped onto the room when the session starts, so once
  // it is live the form can no longer change them and says so.
  const [sessionStatus, setSessionStatus] = useState(null);

  // Pristine snapshot for the Reset action (captured once after initial load).
  const pristineRef = useRef(initialForm);
  const pristineCaptured = useRef(false);

  // Load lookups (instructors / batches / courses)
  useEffect(() => {
    apiClient.get("/instructors/list")
      .then(({ data }) => setInstructors(data.instructors || []))
      .catch(() => {});
    apiClient.get("/batches/list").then(({ data }) => setBatches(data.batches || data.items || [])).catch(() => {});
    apiClient.get("/courses/list").then(({ data }) => setCourses(data.courses || [])).catch(() => {});
    // Not fatal, and deliberately silent: an instructor-scoped token may not
    // read institute settings at all. No answer simply means no provider-
    // specific hint, never a broken form.
    apiClient.get("/institute/zoho-meeting-settings")
      .then(({ data }) => setMeetingProvider(data?.meetingProvider || "zoom"))
      .catch(() => {});
  }, []);

  // Load existing record for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    apiClient.get(`${vocab.apiBase}/sessions/${id}`)
      .then(({ data }) => {
        const s = getDemoSession(data);
        setForm({
          title: s.title || "",
          description: s.description || "",
          scheduledStartAt: toLocalInput(s.scheduledStartAt),
          scheduledEndAt: toLocalInput(s.scheduledEndAt),
          timezone: s.timezone || initialForm.timezone,
          instructorId: s.instructorId?._id || s.instructorId || "",
          // Populated by the API (`{_id, name}`) but plain ids on an older
          // response — both shapes have to survive a round trip through the
          // form, or editing anything at all would drop the co-speakers.
          coHostInstructorIds: (s.coHostInstructorIds || []).map((c) => String(c?._id || c)),
          batchId: s.batchId?._id || s.batchId || "",
          courseId: s.courseId?._id || s.courseId || "",
          joinOpensMinutesBefore: s.joinOpensMinutesBefore ?? 15,
          allowJoinAfterStart: s.allowJoinAfterStart ?? true,
          maxAttendees: s.maxAttendees ?? 100,
          registrationMode: s.registrationMode === "guest" ? "guest" : "preRegister",
          collectEmail: s.collectEmail ?? true,
          collectCity: s.collectCity ?? false,
          consentText: s.consentText || vocab.copy.consentDefault,
          isPublished: s.isPublished ?? true,
          requireCaptcha: s.requireCaptcha ?? false,
          customQuestions: Array.isArray(s.customQuestions)
            ? s.customQuestions.map((q) => ({ ...q, optionsText: (q.options || []).join(", ") }))
            : [],
          branding: { ...initialForm.branding, ...(s.branding || {}) },
          recording: { ...initialForm.recording, ...(s.recording || {}) },
          // Spread over the defaults rather than replacing them: a session
          // created before these existed has no `attendeePolicy` at all, and
          // `undefined` in a checkbox turns a controlled input uncontrolled.
          attendeePolicy: { ...initialForm.attendeePolicy, ...(s.attendeePolicy || {}) },
          ...(isWebinar ? eventFieldsFromSession(s) : {}),
        });
        setSessionStatus(s.status || null);
        if (isWebinar) {
          setPreviews(previewsFromSession(s, data?.eventImages));
          setWebsiteUrl(data?.websiteUrl || null);
        }
      })
      .catch(() => setToast({ show: true, message: `Failed to load ${vocab.fullNoun}.`, type: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, kind]);

  // Snapshot the pristine form once the initial (add / edit-load) state settles.
  useEffect(() => {
    if (!loading && !pristineCaptured.current) {
      pristineRef.current = form;
      pristineCaptured.current = true;
    }
  }, [loading, form]);

  const handleReset = () => {
    setForm(pristineRef.current);
    setErrors({});
  };

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));
  const updateForm = (patch) => setForm((p) => ({ ...p, ...patch }));
  const setPreview = (key, url) => setPreviews((p) => ({ ...p, [key]: url }));
  const setBranding = (k, v) => setForm((p) => ({ ...p, branding: { ...p.branding, [k]: v } }));
  const setRecording = (k, v) => setForm((p) => ({ ...p, recording: { ...p.recording, [k]: v } }));
  const setPolicy = (k, v) =>
    setForm((p) => ({ ...p, attendeePolicy: { ...p.attendeePolicy, [k]: v } }));

  const addQuestion = () =>
    setForm((p) => ({
      ...p,
      customQuestions: [
        ...p.customQuestions,
        { key: `q${p.customQuestions.length + 1}`, label: "", type: "text", required: false, options: [], optionsText: "" },
      ],
    }));
  const updateQuestion = (idx, patch) =>
    setForm((p) => ({
      ...p,
      customQuestions: p.customQuestions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    }));
  const removeQuestion = (idx) =>
    setForm((p) => ({ ...p, customQuestions: p.customQuestions.filter((_, i) => i !== idx) }));

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required.";
    if (!form.scheduledStartAt) e.scheduledStartAt = "Start date/time is required.";
    if (!form.scheduledEndAt) e.scheduledEndAt = "End date/time is required.";
    if (form.scheduledStartAt && form.scheduledEndAt
        && new Date(form.scheduledEndAt) <= new Date(form.scheduledStartAt)) {
      e.scheduledEndAt = "End must be after start.";
    }
    // Consent + custom questions only apply to the pre-registration flow.
    if (form.registrationMode !== "guest") {
      if (!form.consentText.trim()) e.consentText = "Consent text is required.";
      const seenKeys = new Set();
      (form.customQuestions || []).forEach((q, idx) => {
        if (!q.key?.trim() || !q.label?.trim()) e[`q-${idx}`] = "Key and label are required.";
        else if (seenKeys.has(q.key.trim())) e[`q-${idx}`] = "Duplicate question key.";
        else seenKeys.add(q.key.trim());
        if (q.type === "select" && !(q.options || []).filter(Boolean).length) {
          e[`q-${idx}`] = "Select-type questions need at least one option.";
        }
      });
    }
    if (isWebinar) Object.assign(e, validateEventFields(form));
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        scheduledStartAt: fromLocalInput(form.scheduledStartAt),
        scheduledEndAt: fromLocalInput(form.scheduledEndAt),
        instructorId: form.instructorId || null,
        // The primary speaker is the host; listing them again as a co-host
        // would put the same person in the room twice. The BE strips this as
        // well — it is a privilege list and cannot be left to the client —
        // but doing it here keeps the saved record identical to the form.
        coHostInstructorIds: (form.coHostInstructorIds || []).filter(
          (cid) => cid && cid !== form.instructorId
        ),
        batchId: form.batchId || null,
        courseId: form.courseId || null,
        joinOpensMinutesBefore: Number(form.joinOpensMinutesBefore) || 0,
        maxAttendees: Number(form.maxAttendees) || null,
        customQuestions: (form.customQuestions || []).map(({ optionsText, ...q }) => q),
        ...(isWebinar ? eventPayload(form) : {}),
      };
      if (!isWebinar) {
        // Present in the state object only for webinars; never sent for a demo.
        ["eventType", "website", "whatsappGroupUrl", "reminderOffsets", "followUp", "certificate"]
          .forEach((k) => delete payload[k]);
      }
      /* An ended session accepts only what happens NEXT — the backend refuses
         the rest, so the rest is not sent. */
      if (isEdit && sessionStatus === "ended") {
        Object.keys(payload).forEach((k) => { if (!POST_END_FIELDS.includes(k)) delete payload[k]; });
      }
      /* A live session rejects an attendee-policy change with a 409. The form
         still holds the values (it renders them, disabled), so they have to be
         dropped from the request rather than sent and refused — otherwise
         fixing a typo in the title of a running webinar fails. */
      if (isEdit && sessionStatus === "live") delete payload.attendeePolicy;
      let savedId;
      if (isEdit) {
        await apiClient.patch(`${vocab.apiBase}/sessions/${id}`, payload);
        savedId = id;
        setToast({ show: true, message: `${vocab.FullNoun} updated.`, type: "success" });
      } else {
        const { data } = await apiClient.post(`${vocab.apiBase}/sessions`, payload);
        const saved = getDemoSession(data);
        savedId = saved?._id;
        setToast({ show: true, message: `${vocab.FullNoun} created.`, type: "success" });
      }
      if (!savedId) throw new Error(`The ${vocab.noun} was saved, but the response did not include its id.`);
      setTimeout(() => navigate(`${vocab.adminBase}/${savedId}`), 600);
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || "Save failed.",
        type: "error",
      });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="demo-form">
        <div className="demo-form__card">Loading…</div>
      </div>
    );
  }

  const speakerWord = kind === "webinar" ? "speaker" : "instructor";

  // Everyone except whoever is already the primary speaker — offering them as
  // their own co-host is the one selection that can never mean anything.
  const instructorNames = new Map(instructors.map((i) => [String(i._id), i.name]));
  const coHostOptions = instructors
    .filter((i) => String(i._id) !== String(form.instructorId))
    .map((i) => ({ value: String(i._id), label: i.name }));

  /* Built from the FORM's ids, not by filtering the options.
   *
   * Filtering the options drops any id the lookup does not contain, and the
   * lookup is empty for the first moment of every page load and missing anyone
   * who has since been deactivated. Someone editing the title of a webinar
   * would have removed a speaker without seeing a chip disappear. */
  const selectedCoHosts = (form.coHostInstructorIds || []).map((cid) => ({
    value: cid,
    label: instructorNames.get(cid) || "Speaker no longer listed",
  }));

  /* Once the room exists, its policy is fixed: the platform was told what
     attendees may do when the session started, and re-saving the form would
     change the words on this page and nothing in the room. The BE refuses it
     too — this only makes the refusal visible before it happens. */
  const policyLocked = isEdit && sessionStatus === "live";
  // `null` = we could not read the setting, so claim nothing either way.
  const policyIsAdvisory = meetingProvider != null && meetingProvider !== "grats";

  // What happened is fixed once a session ends; what happens NEXT is not. The
  // form narrows to exactly what the backend still accepts (POST_END_FIELDS).
  const ended = isEdit && sessionStatus === "ended";
  const hostNames = [form.instructorId, ...(form.coHostInstructorIds || [])]
    .map((hid) => instructorNames.get(String(hid)))
    .filter(Boolean);
  const eventSectionProps = { form, update: updateForm, previews, setPreview, errors, noun: vocab.noun, ended };

  return (
    <form className="demo-form has-form-actionbar" onSubmit={handleSubmit} noValidate>
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        backButton
        icon={PlayCircle}
        title={`${isEdit ? "Edit" : "New"} ${vocab.FullNoun}`}
        description={vocab.copy.formDescription}
        onBack={() => navigate(-1)}
      />

      {ended && (
        <Callout tone="info">
          This {vocab.noun} has ended. You can still change its event page, follow-ups, certificate and recording.
        </Callout>
      )}

      {isWebinar && !ended && canManage && (
        <EventAiPanel
          form={form}
          onApply={(next) => { setForm(next); setErrors({}); }}
          instructors={instructors}
          courses={courses}
          apiBase={vocab.apiBase}
          scheduleLocked={isEdit && sessionStatus === "live"}
          saveLabel={`${isEdit ? "Update" : "Create"} ${vocab.noun}`}
          startOpen={!isEdit}
        />
      )}

      {!ended && (
      <>
      <div className="demo-form__card">
        <div className="demo-form__grid">
          <div className="demo-form__field">
            <label htmlFor="title">Title *</label>
            <input id="title" type="text" value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder={vocab.copy.titlePlaceholder} />
            {errors.title && <span className="demo-form__hint demo-form__hint--error">{errors.title}</span>}
          </div>
          <div className="demo-form__field">
            <label htmlFor="instructor">{kind === "webinar" ? "Speaker" : "Instructor"}</label>
            <select id="instructor" value={form.instructorId} onChange={(e) => setField("instructorId", e.target.value)}>
              <option value="">— Select {kind === "webinar" ? "speaker" : "instructor"} —</option>
              {instructors.map((i) => (
                <option key={i._id} value={i._id}>{i.name}</option>
              ))}
            </select>
          </div>
          <div className="demo-form__field demo-form__field--full">
            <label htmlFor="cohosts">
              Additional {kind === "webinar" ? "speakers" : "instructors"}
            </label>
            <Select
              inputId="cohosts"
              isMulti
              options={coHostOptions}
              value={selectedCoHosts}
              onChange={(opts) =>
                setField("coHostInstructorIds", (opts || []).map((o) => o.value))
              }
              placeholder={`Add anyone else presenting this ${vocab.noun}…`}
              isClearable
              closeMenuOnSelect={false}
              noOptionsMessage={() => "No other instructors found"}
              className="demo-form__multiselect"
              styles={reactSelectTheme()}
              // Portaled to <body>, or the menu is clipped by the card.
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            />
            <span className="demo-form__hint">
              They join the live room with the same rights as the {speakerWord} —
              microphone, camera, screen share and the ability to mute or unmute
              attendees — and the {vocab.noun} appears in their own list.
            </span>
          </div>
          <div className="demo-form__field demo-form__field--full">
            <label htmlFor="description">Description</label>
            <textarea id="description" rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="What will attendees learn? Why should they join?" />
          </div>
        </div>
      </div>

      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Schedule</h4>
        <div className="demo-form__grid">
          <div className="demo-form__field">
            <label htmlFor="start">Start *</label>
            <input id="start" type="datetime-local" value={form.scheduledStartAt} onChange={(e) => setField("scheduledStartAt", e.target.value)} />
            {errors.scheduledStartAt && <span className="demo-form__hint demo-form__hint--error">{errors.scheduledStartAt}</span>}
          </div>
          <div className="demo-form__field">
            <label htmlFor="end">End *</label>
            <input id="end" type="datetime-local" value={form.scheduledEndAt} onChange={(e) => setField("scheduledEndAt", e.target.value)} />
            {errors.scheduledEndAt && <span className="demo-form__hint demo-form__hint--error">{errors.scheduledEndAt}</span>}
          </div>
          <div className="demo-form__field">
            <label htmlFor="tz">Timezone</label>
            <input id="tz" type="text" value={form.timezone} onChange={(e) => setField("timezone", e.target.value)} />
          </div>
          <div className="demo-form__field">
            <label htmlFor="opens">Join opens (minutes before)</label>
            <input id="opens" type="number" min="0" max="180" value={form.joinOpensMinutesBefore} onChange={(e) => setField("joinOpensMinutesBefore", e.target.value)} />
          </div>
          <div className="demo-form__field">
            <label htmlFor="max">Max attendees</label>
            <input id="max" type="number" min="1" value={form.maxAttendees} onChange={(e) => setField("maxAttendees", e.target.value)} />
          </div>
        </div>
        <div className="demo-form__checkbox-row demo-form__checkbox-row--spaced">
          <label>
            <input type="checkbox" checked={form.allowJoinAfterStart} onChange={(e) => setField("allowJoinAfterStart", e.target.checked)} />
            Allow late joiners after the {vocab.noun} has started
          </label>
        </div>
      </div>

      </>
      )}

      {isWebinar && (
        <>
          <EventPageSection {...eventSectionProps} websiteUrl={websiteUrl} title={form.title} />
          <EventProgramSection {...eventSectionProps} hostNames={hostNames} />
        </>
      )}

      {!ended && (
      <>
      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Linked content (optional)</h4>
        <div className="demo-form__grid">
          <div className="demo-form__field">
            <label htmlFor="batch">Batch</label>
            <select id="batch" value={form.batchId} onChange={(e) => setField("batchId", e.target.value)}>
              <option value="">— None —</option>
              {batches.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
            <span className="demo-form__hint">Used as a hint in the post-session follow-up form.</span>
          </div>
          <div className="demo-form__field">
            <label htmlFor="course">Course</label>
            <select id="course" value={form.courseId} onChange={(e) => setField("courseId", e.target.value)}>
              <option value="">— None —</option>
              {courses.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Attendee access</h4>
        <p className="demo-form__hint">Choose how attendees join this {vocab.noun}.</p>
        <div className="demo-form__mode">
          <label className={`demo-form__mode-option${form.registrationMode === "preRegister" ? " demo-form__mode-option--active" : ""}`}>
            <input
              type="radio"
              name="registrationMode"
              checked={form.registrationMode === "preRegister"}
              onChange={() => setField("registrationMode", "preRegister")}
            />
            <span className="demo-form__mode-option-body">
              <span className="demo-form__mode-option-title">Pre-registration required</span>
              <span className="demo-form__mode-option-desc">
                Collect name, mobile &amp; consent (plus any custom questions) before joining.
                Enables reminders, lead capture and CRM sync.
              </span>
            </span>
          </label>
          <label className={`demo-form__mode-option${form.registrationMode === "guest" ? " demo-form__mode-option--active" : ""}`}>
            <input
              type="radio"
              name="registrationMode"
              checked={form.registrationMode === "guest"}
              onChange={() => setField("registrationMode", "guest")}
            />
            <span className="demo-form__mode-option-body">
              <span className="demo-form__mode-option-title">Join as guest</span>
              <span className="demo-form__mode-option-desc">
                Frictionless — attendees enter only their name. No mobile, consent, reminders or CRM sync.
              </span>
            </span>
          </label>
        </div>
        <div className="demo-form__checkbox-row demo-form__checkbox-row--spaced">
          <label>
            <input type="checkbox" checked={form.isPublished} onChange={(e) => setField("isPublished", e.target.checked)} />
            Published (publicly accessible)
          </label>
        </div>
      </div>

      <div className="demo-form__card">
        <h4 className="demo-form__section-title">What attendees can do in the live room</h4>
        <p className="demo-form__hint">
          Applies to attendees only. The {speakerWord} and any additional{" "}
          {kind === "webinar" ? "speakers" : "instructors"} always keep their
          microphone, camera and screen share, and can unmute an individual
          attendee to take a question.
        </p>

        {policyLocked && (
          <p className="demo-form__hint demo-form__hint--error">
            This {vocab.noun} is live. These permissions were applied to the room
            when it started and cannot be changed from here — the host can mute
            or unmute people inside the room instead.
          </p>
        )}

        <div className="demo-form__policy">
          {ATTENDEE_PERMISSIONS.map(({ key, label, hint }) => (
            <label key={key} className="demo-form__policy-row">
              <input
                type="checkbox"
                checked={!!form.attendeePolicy[key]}
                disabled={policyLocked}
                onChange={(e) => setPolicy(key, e.target.checked)}
              />
              <span className="demo-form__policy-body">
                <span className="demo-form__policy-label">{label}</span>
                <span className="demo-form__hint">{hint}</span>
              </span>
            </label>
          ))}

          {/* Only meaningful when they may unmute at all — otherwise it is a
              toggle that describes the same silence either way. */}
          {form.attendeePolicy.allowMicrophone && (
            <label className="demo-form__policy-row demo-form__policy-row--nested">
              <input
                type="checkbox"
                checked={!!form.attendeePolicy.startMuted}
                disabled={policyLocked}
                onChange={(e) => setPolicy("startMuted", e.target.checked)}
              />
              <span className="demo-form__policy-body">
                <span className="demo-form__policy-label">Start muted on entry</span>
                <span className="demo-form__hint">
                  Recommended: they can unmute themselves, but do not walk in mid-sentence.
                </span>
              </span>
            </label>
          )}
        </div>

        {policyIsAdvisory && (
          <p className="demo-form__hint demo-form__hint--warning">
            Your live classes run on {meetingProvider === "zoho" ? "Zoho" : "Zoom"},
            where these are the settings attendees <em>join</em> with rather than
            limits they cannot lift — and chat cannot be restricted before the
            meeting starts at all. Switch the meeting provider to Grats Meet
            (Settings → Live classes) to have them enforced.
          </p>
        )}
      </div>

      {form.registrationMode === "guest" ? (
        <div className="demo-form__card">
          <h4 className="demo-form__section-title">Registration form</h4>
          <p className="demo-form__hint">
            Guest mode is on — the public page collects only the attendee's name.
            Email, city, consent and custom questions are skipped. Switch to
            "Pre-registration required" above to capture full details.
          </p>
        </div>
      ) : (
      <>
      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Registration form</h4>
        <div className="demo-form__checkbox-row">
          <label>
            <input type="checkbox" checked={form.collectEmail} onChange={(e) => setField("collectEmail", e.target.checked)} />
            Collect email address
          </label>
          <label>
            <input type="checkbox" checked={form.collectCity} onChange={(e) => setField("collectCity", e.target.checked)} />
            Collect city
          </label>
          <label>
            <input type="checkbox" checked={form.requireCaptcha} onChange={(e) => setField("requireCaptcha", e.target.checked)} />
            Require captcha (Cloudflare Turnstile)
          </label>
        </div>
        <div className="demo-form__field demo-form__field--full">
          <label htmlFor="consent">Consent text *</label>
          <textarea id="consent" rows={2} value={form.consentText} onChange={(e) => setField("consentText", e.target.value)} />
          {errors.consentText && <span className="demo-form__hint demo-form__hint--error">{errors.consentText}</span>}
        </div>
      </div>

      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Custom questions</h4>
        <p className="demo-form__hint">
          Optional fields shown on the registration form (e.g. "Current role", "Experience").
        </p>
        {form.customQuestions.map((q, idx) => (
          <div key={idx} className="demo-form__grid demo-form__question-row">
            <div className="demo-form__field">
              <label>Key</label>
              <input
                type="text"
                value={q.key}
                onChange={(e) => updateQuestion(idx, { key: e.target.value.replace(/[^a-z0-9_]/gi, "") })}
                placeholder="role"
              />
            </div>
            <div className="demo-form__field">
              <label>Label</label>
              <input
                type="text"
                value={q.label}
                onChange={(e) => updateQuestion(idx, { label: e.target.value })}
                placeholder="What is your current role?"
              />
            </div>
            <div className="demo-form__field">
              <label>Type</label>
              <select value={q.type} onChange={(e) => updateQuestion(idx, { type: e.target.value })}>
                <option value="text">Text</option>
                <option value="textarea">Long text</option>
                <option value="select">Select</option>
                <option value="checkbox">Checkbox</option>
              </select>
            </div>
            {q.type === "select" && (
              <div className="demo-form__field demo-form__field--full">
                <label>Options (comma-separated)</label>
                <input
                  type="text"
                  value={q.optionsText ?? (q.options || []).join(", ")}
                  onChange={(e) =>
                    updateQuestion(idx, {
                      optionsText: e.target.value,
                      options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  placeholder="Student, Working professional, Other"
                />
              </div>
            )}
            <div className="demo-form__field demo-form__question-actions">
              <label>
                <input
                  type="checkbox"
                  checked={!!q.required}
                  onChange={(e) => updateQuestion(idx, { required: e.target.checked })}
                />{" "}
                Required
              </label>
              <AppButton type="button" variant="ghost" size="sm" onClick={() => removeQuestion(idx)}>
                <Trash2 size={14} /> Remove
              </AppButton>
            </div>
            {errors[`q-${idx}`] && (
              <span className="demo-form__hint demo-form__hint--error demo-form__field--full">
                {errors[`q-${idx}`]}
              </span>
            )}
          </div>
        ))}
        <AppButton type="button" variant="outline" size="sm" onClick={addQuestion}>
          <Plus size={14} /> Add question
        </AppButton>
      </div>
      </>
      )}
      </>
      )}

      {isWebinar && (
        <>
          <EventAutomationSection {...eventSectionProps} />
          <EventCertificateSection {...eventSectionProps} sessionId={isEdit ? id : null} apiBase={vocab.apiBase} />
        </>
      )}

      {!ended && (
      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Branding (overrides institute defaults)</h4>
        <div className="demo-form__grid">
          <div className="demo-form__field">
            <label>Logo URL</label>
            <input
              type="url"
              value={form.branding.logoUrl || ""}
              onChange={(e) => setBranding("logoUrl", e.target.value)}
              placeholder="https://…/logo.png"
            />
          </div>
          {/* A webinar's cover is set on its event page, where it can be uploaded. */}
          {!isWebinar && (
          <div className="demo-form__field">
            <label>Cover image URL</label>
            <input
              type="url"
              value={form.branding.coverImageUrl || ""}
              onChange={(e) => setBranding("coverImageUrl", e.target.value)}
              placeholder="https://…/cover.jpg"
            />
          </div>
          )}
          <div className="demo-form__field">
            <label>Primary colour</label>
            <input
              type="text"
              value={form.branding.primaryColor || ""}
              onChange={(e) => setBranding("primaryColor", e.target.value)}
              placeholder="#2563eb"
            />
          </div>
          <div className="demo-form__field">
            <label>Tagline</label>
            <input
              type="text"
              value={form.branding.tagline || ""}
              onChange={(e) => setBranding("tagline", e.target.value)}
              placeholder={vocab.copy.shellLabel}
            />
          </div>
        </div>
      </div>

      )}

      <div className="demo-form__card">
        <h4 className="demo-form__section-title">Recording</h4>
        <div className="demo-form__checkbox-row">
          <label>
            <input
              type="checkbox"
              checked={!!form.recording.enabled}
              onChange={(e) => setRecording("enabled", e.target.checked)}
            />
            Record this {vocab.noun}
          </label>
        </div>
        {form.recording.enabled && (
          <div className="demo-form__grid">
            <div className="demo-form__field">
              <label>Visibility after the {vocab.noun}</label>
              <select
                value={form.recording.visibility}
                onChange={(e) => setRecording("visibility", e.target.value)}
              >
                <option value="admin">Admin only</option>
                <option value="attendees">Share with attendees</option>
              </select>
            </div>
            {form.recording.visibility === "attendees" && (
              <div className="demo-form__field demo-form__field--full">
                <label>Recording link (optional)</label>
                <input
                  type="url"
                  value={form.recording.url || ""}
                  onChange={(e) => setRecording("url", e.target.value)}
                  placeholder="https://…"
                />
                <span className="demo-form__hint">
                  On Grats Meet, leave this empty: attendees get the recording saved in the LMS.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed bottom action bar */}
      <FormActionBar>
        <AppButton variant="outline" type="button" onClick={() => navigate(-1)}>
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} />
        {canManage && (
          <AppButton variant="default" type="submit" disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : `${isEdit ? "Update" : "Create"} ${vocab.noun}`}
          </AppButton>
        )}
      </FormActionBar>
    </form>
  );
};

export default DemoClassesFormPage;
